import { type FC } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
    Users,
    Receipt,
    HomeIcon,
    UserIcon,
    CoinsIcon,
    FlagIcon,
} from 'lucide-react'
import Ogaboss from '../../../public/ogaboss.svg'

const navigation = [
    {
        name: 'Dashboard',
        href: '/',
        icon: HomeIcon,
        section: 'customer',
    },
    {
        name: 'Users',
        href: '/users',
        icon: UserIcon,
        section: 'customer',
    },
    {
        name: 'Transactions',
        href: '/transactions',
        icon: Receipt,
        section: 'customer',
    },
    {
        name: 'Users',
        href: '/internal-users',
        icon: Users,
        section: 'internal',
    },
    {
        name: 'Rates',
        href: '/rates',
        icon: CoinsIcon,
        section: 'internal',
    },
    {
        name: 'Feature flags',
        href: '/feature-flags',
        icon: FlagIcon,
        section: 'internal',
    },
]

interface SidebarProps {
    className?: string
}

const Sidebar: FC<SidebarProps> = ({ className }) => {
    const location = useLocation()

    return (
        <aside
            className={cn(
                'fixed left-0 top-0 z-30 h-screen w-64 border-r border-border bg-card',
                className
            )}
        >
            <div className="flex h-full flex-col">
                {/* Logo */}
                <div className="flex h-20 items-center gap-2 border-b border-border px-6">
                    <img alt="Ogaboss Icon" src={Ogaboss} />
                    <div>
                        <h2 className="text-sm font-extrabold text-[#0E121B]">
                            Ogaboss
                        </h2>
                        <p className="text-xs text-[#717784]">
                            Admin Management
                        </p>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 space-y-6 overflow-y-auto p-4">
                    {/* Customer Section */}
                    <div className="space-y-1">
                        <h3 className="mb-2 px-2 text-xs font-medium uppercase text-muted-foreground">
                            Customer
                        </h3>
                        {navigation
                            .filter(item => item.section === 'customer')
                            .map(item => {
                                const isActive =
                                    location.pathname === item.href ||
                                    (item.href !== '/' &&
                                        location.pathname.startsWith(item.href))

                                return (
                                    <Link
                                        key={item.name + item.section}
                                        to={item.href}
                                        className={cn(
                                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                            isActive
                                                ? 'bg-primary text-primary-foreground'
                                                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                        )}
                                    >
                                        <item.icon className="h-4 w-4" />
                                        {item.name}
                                    </Link>
                                )
                            })}
                    </div>

                    {/* Internal Section */}
                    <div className="space-y-1">
                        <h3 className="mb-2 px-2 text-xs font-medium uppercase text-muted-foreground">
                            Internal
                        </h3>
                        {navigation
                            .filter(item => item.section === 'internal')
                            .map(item => {
                                const isActive =
                                    location.pathname === item.href ||
                                    (item.href !== '/' &&
                                        location.pathname.startsWith(item.href))

                                return (
                                    <Link
                                        key={item.name + item.section}
                                        to={item.href}
                                        className={cn(
                                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                            isActive
                                                ? 'bg-primary text-primary-foreground'
                                                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                        )}
                                    >
                                        <item.icon className="h-4 w-4" />
                                        {item.name}
                                    </Link>
                                )
                            })}
                    </div>
                </nav>
            </div>
        </aside>
    )
}

export default Sidebar
